/**
 * Plan × feature matrix.  Mirrors the backend's `Organization.PLAN_LIMITS`
 * exactly (see audit 09a §4 + subscribe.json + menu.json).  Any drift is
 * a spec bug — backend is the source of truth; this module stays in sync
 * as PLAN_LIMITS evolves.
 *
 * Consumers use the `canUse(feature, user)` helper (canUse.ts) rather
 * than reading this object directly — it also handles role + quota gates.
 */
import type { Plan } from '@/lib/auth';

/**
 * Every feature flag the UI consults.  Matches PLAN_LIMITS keys and the
 * `feature_keys_used` set in docs/registered-users/menu.json:fixture_meta.
 *
 * Naming note: spec uses `compliance_calendar` (not `compliance`) and
 * `on_prem` (not `onprem`).  We follow the spec verbatim so menu-driven
 * gating and backend gating share one vocabulary.
 */
export type Feature =
  | 'ai_redlining'
  | 'ai_research'
  | 'ai_memory'
  | 'court_tools'
  | 'traces_access'
  | 'compliance_calendar'
  | 'sebi_dashboard'
  | 'tax'
  | 'voice'
  | 'custom_agents'
  | 'org_analytics'
  | 'bulk_analysis'
  | 'white_label'
  | 'saml_sso'
  | 'global_layer'
  | 'custom_model_training'
  | 'scim'
  | 'firm_preferences'
  | 'intelligence_api'
  | 'vault_sharing'
  | 'vault_version_history'
  | 'earnings_call_qa'
  | 'on_prem'
  /** Pro+ — counter-offer drafting + AI negotiation email drafts. */
  | 'negotiation'
  /** Pro+ — org-wide legal health score + monthly history graph. */
  | 'health_score'
  /** Pro+ — clause-level litigation-risk scoring on contracts. */
  | 'litigation_risk'
  /** Inline UI affordance, NOT a menu item — gates the "Apply Fix" button on risk reports. */
  | 'fix_suggestions';

type PlanFeatureRow = Readonly<Record<Feature, boolean>>;

/**
 * The desk's subset of the backend's `INDIA_ONLY_FEATURE_KEYS`
 * (app/seeds/subscription_plans.py). Every route behind these keys is
 * mounted `require_india_edition` and 404s for a global org, so no global
 * org may ever be told it can use one — whatever its plan row or the
 * server's feature list says (with `open_all_features` on, the server's
 * `/users/me/features` lists every active key, India-only ones included).
 */
export const INDIA_ONLY_FEATURES: ReadonlySet<Feature> = new Set<Feature>([
  'ai_research',
  'compliance_calendar',
  'court_tools',
  'litigation_risk',
  'sebi_dashboard',
  'tax',
  'traces_access',
]);

/** Default-everything-false row used as a base for plan entries. */
const NONE: PlanFeatureRow = {
  ai_redlining: false,
  ai_research: false,
  ai_memory: false,
  court_tools: false,
  traces_access: false,
  compliance_calendar: false,
  sebi_dashboard: false,
  tax: false,
  voice: false,
  custom_agents: false,
  org_analytics: false,
  bulk_analysis: false,
  white_label: false,
  saml_sso: false,
  global_layer: false,
  custom_model_training: false,
  scim: false,
  firm_preferences: false,
  intelligence_api: false,
  vault_sharing: false,
  vault_version_history: false,
  earnings_call_qa: false,
  on_prem: false,
  negotiation: false,
  health_score: false,
  litigation_risk: false,
  fix_suggestions: false,
};

/** `pro` with every India-only key off — the backend's `global_matrix`. */
const GLOBAL_ROW: PlanFeatureRow = {
  ...NONE,
  fix_suggestions: true,
  vault_sharing: true,
  vault_version_history: true,
  ai_redlining: true,
  bulk_analysis: true,
  voice: true,
  negotiation: true,
  health_score: true,
};

/** Per-plan feature availability. */
export const PLAN_FEATURES: Record<Plan, PlanFeatureRow> = {
  free: { ...NONE },

  // Canonical post-migration plan (subscribe.json) — fix-suggestions +
  // shareable links only.
  basic: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
  },

  // Canonical Pro tier — adds AI redline + bulk + voice + compliance
  // calendar + version history.  Mirrors subscribe.json's `pro` plan.
  // NOTE: `custom_agents` is enterprise-only per menu.json
  // trial_behaviour, so it stays false here.
  pro: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    bulk_analysis: true,
    voice: true,
    compliance_calendar: true,
    tax: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // Canonical Enterprise — everything on.
  enterprise: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    ai_memory: true,
    court_tools: true,
    traces_access: true,
    compliance_calendar: true,
    sebi_dashboard: true,
    tax: true,
    voice: true,
    custom_agents: true,
    org_analytics: true,
    bulk_analysis: true,
    white_label: true,
    saml_sso: true,
    global_layer: true,
    custom_model_training: true,
    scim: true,
    firm_preferences: true,
    intelligence_api: true,
    earnings_call_qa: true,
    on_prem: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // Trial — mirrors Pro per auth-register.json:trial_behaviour.
  // Enterprise-only flags (custom_agents, scim, etc.) stay off per
  // menu.json trial_behaviour explicit "hide enterprise-only items".
  trial: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    bulk_analysis: true,
    voice: true,
    compliance_calendar: true,
    tax: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // ── Legacy / grandfathered slugs (mirror canonical equivalents) ─────
  // 'starter' → basic-equivalent
  starter: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
  },

  // 'student' → basic-equivalent
  student: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
  },

  // 'freelancer_basic' → basic-equivalent
  freelancer_basic: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
  },

  // 'solo' → basic-equivalent
  solo: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
  },

  // 'professional' → pro-equivalent (legacy SOLO-with-AI tier)
  professional: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // 'freelancer_pro' → pro-equivalent + tax
  freelancer_pro: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    tax: true,
  },

  // 'ca_cs' — CA/CS vertical, pro-equivalent + traces + compliance
  ca_cs: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    traces_access: true,
    compliance_calendar: true,
    sebi_dashboard: true,
    tax: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // 'advocate' — Advocate vertical, pro-equivalent + court tools
  advocate: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    court_tools: true,
    compliance_calendar: true,
    tax: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // 'team' — multi-seat tier, enterprise-light (no on-prem / SCIM / etc).
  team: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    court_tools: true,
    compliance_calendar: true,
    sebi_dashboard: true,
    tax: true,
    voice: true,
    custom_agents: true,
    org_analytics: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // 'business' — multi-seat tier with white-label + SSO + global.
  business: {
    ...NONE,
    fix_suggestions: true,
    vault_sharing: true,
    vault_version_history: true,
    ai_redlining: true,
    ai_research: true,
    court_tools: true,
    compliance_calendar: true,
    sebi_dashboard: true,
    tax: true,
    voice: true,
    custom_agents: true,
    org_analytics: true,
    bulk_analysis: true,
    white_label: true,
    saml_sso: true,
    global_layer: true,
    negotiation: true,
    health_score: true,
    litigation_risk: true,
  },

  // ── Global edition (USD, Stripe) ──────────────────────────────────────
  // The backend seed gives all four global plans the `pro` matrix with
  // every INDIA_ONLY_FEATURE_KEYS entry switched off (the only other
  // difference, privacy_compliance off on global_starter, is not a desk
  // Feature). Until 2026-10-11 these rows were missing, so every paying
  // global org failed the plan lookup and saw "Upgrade to Pro" on AI
  // redline, bulk upload, version history, shared contracts, …
  global_starter: GLOBAL_ROW,
  global_pro: GLOBAL_ROW,
  global_business: GLOBAL_ROW,
  global_team: GLOBAL_ROW,
};

/**
 * For each feature, the cheapest plan that unlocks it.  Used for the
 * "Upgrade to X" CTA when a `canUse` check fails.
 */
export const FEATURE_MIN_PLAN: Record<Feature, Plan> = {
  fix_suggestions: 'basic',
  vault_sharing: 'basic',
  ai_redlining: 'pro',
  ai_research: 'pro',
  bulk_analysis: 'pro',
  voice: 'pro',
  compliance_calendar: 'pro',
  vault_version_history: 'pro',
  custom_agents: 'pro',
  tax: 'pro',
  negotiation: 'pro',
  health_score: 'pro',
  litigation_risk: 'pro',
  court_tools: 'advocate',
  traces_access: 'ca_cs',
  sebi_dashboard: 'ca_cs',
  org_analytics: 'team',
  white_label: 'business',
  saml_sso: 'business',
  global_layer: 'business',
  ai_memory: 'enterprise',
  custom_model_training: 'enterprise',
  scim: 'enterprise',
  firm_preferences: 'enterprise',
  intelligence_api: 'enterprise',
  earnings_call_qa: 'enterprise',
  on_prem: 'enterprise',
};

/**
 * Map a (possibly legacy) plan slug to its canonical post-migration
 * equivalent (subscribe.json `canonical_plan_names`).  Use this at the
 * boundary — wherever we read `org.plan` and want to branch on the
 * canonical three (`basic` / `pro` / `enterprise`) without a giant
 * switch over every legacy variant.
 *
 * Mapping rationale:
 *   - free / trial               → 'free' / 'trial' (no charge tier)
 *   - solo / starter / student / freelancer_basic → 'basic'
 *     (single-seat, sub-Pro feature set)
 *   - professional / freelancer_pro / ca_cs / advocate / pro → 'pro'
 *   - team / business / enterprise → 'enterprise'
 *     (multi-seat tiers fold into the enterprise contact-sales path)
 *
 * Returns the input unchanged when it is already canonical.
 */
export type CanonicalPlan = 'free' | 'trial' | 'basic' | 'pro' | 'enterprise';

const LEGACY_TO_CANONICAL: Partial<Record<Plan, CanonicalPlan>> = {
  starter: 'basic',
  solo: 'basic',
  student: 'basic',
  freelancer_basic: 'basic',
  professional: 'pro',
  freelancer_pro: 'pro',
  ca_cs: 'pro',
  advocate: 'pro',
  team: 'enterprise',
  business: 'enterprise',
  global_starter: 'basic',
  global_pro: 'pro',
  global_business: 'enterprise',
  global_team: 'enterprise',
};

export function canonicalPlan(plan: Plan): CanonicalPlan {
  if (plan === 'free' || plan === 'trial' || plan === 'basic' || plan === 'pro' || plan === 'enterprise') {
    return plan;
  }
  return LEGACY_TO_CANONICAL[plan] ?? 'basic';
}

/**
 * True when the user is in an active trial (plan_id is null AND
 * trial_expires_at is in the future).  Per menu.json:trial_behaviour
 * such users are shown Pro-equivalent items — enterprise-only items
 * stay hidden because they require a paid plan.
 */
export function isInTrial(user: { org: { plan_id: string | null; trial_expires_at: string | null } } | null | undefined): boolean {
  if (!user) return false;
  if (user.org.plan_id !== null) return false;
  if (!user.org.trial_expires_at) return false;
  return new Date(user.org.trial_expires_at).getTime() > Date.now();
}

/** Human-readable plan label for UI. */
export const PLAN_LABEL: Record<Plan, string> = {
  trial: 'Trial',
  free: 'Free',
  basic: 'Basic',
  starter: 'Starter',
  professional: 'Professional',
  student: 'Student',
  freelancer_basic: 'Freelancer Basic',
  solo: 'Solo',
  freelancer_pro: 'Freelancer Pro',
  pro: 'Pro',
  ca_cs: 'CA / CS',
  advocate: 'Advocate',
  team: 'Team',
  business: 'Business',
  enterprise: 'Enterprise',
  global_starter: 'Starter',
  global_pro: 'Pro',
  global_business: 'Business',
  global_team: 'Team',
};
